import React, { useEffect, useState, useRef } from 'react'
import { useQuery } from 'react-query'
import { useDebouncedCallback } from 'use-debounce'
import { getResourceList, getResource } from '../models/GraphQLModel'
import { getCarouselOptions } from '@scrippsproduct/networks-ui-library/dist/utils/getCarouselOptions'
import SEO from '../utils/SEOMetaTags'
import { 
    Carousel, 
    PromotionsRowModule,
    ModalContainer, 
    GeneralErrorBoundary, 
    ErrorHandler
} from '@scrippsproduct/networks-ui-library'
import NowPlayingContainer from '@scrippsproduct/networks-ui-library/dist/containers/NowPlayingContainer'
import GetFeaturedListContainer from '@scrippsproduct/networks-ui-library/dist/containers/GetFeatruedListContainer'
import UpcomingModalContainer from '../utils/UpcomingModalContainer'
import fallbackImage from './../images/Laff_Backup_800x450.jpg'
import { SUPPORT_LINK } from '../settings/Settings'

import './../scss/Home.scss'

const SCHEDULE_ENDPOINT = process.env.REACT_APP_SCHEDULE_V2
const NETWORK = process.env.REACT_APP_NETWORK
const ProgramAirdateProps = { 
    brand: 'Laff', 
    buttonStyle: 'flat', 
    logoStyle: 'image', 
    useLogo: false,
    href: '/schedule',
}
const FALLBACK_IMAGE = {
    url: fallbackImage,
    width: 800,
    height: 450,
}
const mediaQueryList = window.matchMedia('(width <= 800px)')

export default function Home() {
    const {isError, isSuccess, data} = useQuery(['homePage', {resourceName: 'homepage'}], getResourceList)
    const modalProps = {
        contentComponent: 'BCVideoPlayer',
    }
    const lastVideo = useRef(null)
    const [carousels, setCarousels] = useState({
        bannerCarousels: [],
        featuredCarousels: [],
        promoCarousels: [],
        twoColumnCarousels: []
    })
    const errorMessage = `Please try again later. If the problem persists please contact us at <a href="${SUPPORT_LINK}">our support page</a>.`

    /**
     * Handle click events from child components
     * @param {object} eventDetails 
     */
    const handleClicks = useDebouncedCallback(eventDetails => {
        if (eventDetails.type === 'play-video' || (eventDetails.type === 'card-clicked' && eventDetails?.ctaType === 'play-video')) {  
            const modalComponentProps = {
                bcId: eventDetails.resourceId,
                autoplay: true,
                BC_AID: process.env.REACT_APP_BC_ACCOUNTID,
                BC_PLAYERID: process.env.REACT_APP_BC_PLAYERID,
                BC_EMBEDID: process.env.REACT_APP_BC_EMBEDID,    
            }
            const event = new CustomEvent('scripps:modalcontainer:change',  {
                detail: {
                    componentProps: {
                        ...modalComponentProps,
                    },
                    forceOpen: (lastVideo.current === eventDetails.resourceId) ? false : true,
                }
            })
            document.getElementById('modal-container').dispatchEvent(event)
            document.querySelector('body').style.overflow = 'hidden'
        } else if (eventDetails.type === 'card-clicked' && eventDetails?.ctaType === 'show-modal') { 
            const modalComponentProps = {
                programId: eventDetails.resourceId,
                programType: eventDetails.resourceType,
            }
            const event = new CustomEvent('scripps:upcomingmodal:change',  {
                detail: {
                    componentProps: {
                        ...modalComponentProps,
                    },
                    forceOpen: false,
                }
            })
            document.getElementById('upcoming-modal').dispatchEvent(event)
            document.querySelector('body').style.overflow = 'hidden'
        }
    }, 200)
    /**
     * Make sure the body scroll is enabled when the modal is closed
     * @returns void
     */
    const onModalClose = () => {
        document.querySelector('body').style.overflow = 'auto'
    }
    /**
     * Render the page sections
     * @returns React.element
     */
    const renderSections = () => {
        // Helper function to render the carousel section with the appropriate markup and styling for each type
        const renderCarousel = (carousel, index) => {
            const { type, carouselName, slides } = carousel;
            const options = getCarouselOptions(type, carouselName, process.env.REACT_APP_ENV);
    
            switch (type) {
                case 'BANNER':
                    return (
                        <GeneralErrorBoundary 
                            errorTitle="Sorry, we're having trouble with this carousel."
                            errorMessage={errorMessage}
                            key={`banner-carousel-${index}-boundry`}
                        >
                            <content-constrainer class="content-constrainer--lead-container banner-carousel-constrainer" key={index}>
                                <Carousel id="homepage-banner" slides={slides} options={options} />
                            </content-constrainer>
                        </GeneralErrorBoundary>
                    )
                case 'FEATURED':
                    return (
                        <section className='page-section featured-shows-section' key={index}>
                            <GeneralErrorBoundary 
                                errorTitle="Sorry, we're having trouble with this carousel."
                                errorMessage={errorMessage}
                            >
                                <content-constrainer class="content-constrainer--no-border">
                                    <h2 className='h2 h2--extra-bold'>{carouselName}</h2>
                                    <Carousel id="featured-shows" slides={slides} options={options} />
                                </content-constrainer>
                                <hr />
                            </GeneralErrorBoundary>
                        </section>
                    )
                case 'TWO_COLUMN':
                    return (
                        <section className="page-section brand-promotion-carousel" key={index}>
                            <GeneralErrorBoundary 
                                errorTitle="Sorry, we're having trouble with this carousel."
                                errorMessage={errorMessage}
                                key={`banner-carousel-${index}-boundry`}
                            >
                                <content-constrainer class="content-constrainer--no-border">
                                    { (window.matchMedia('(min-width: 801px)').matches) && <Carousel id="brand-promotion" slides={slides} options={options} /> }
                                    { (window.matchMedia('(max-width: 800px)').matches) && <ul className='brand-promotion__list'>
                                        { slides.map(item => {
                                            return <li className="brand-promotion__item" key={item.title}>
                                                { item.props.columnTwoContent }
                                                { item.props.columnOneContent }
                                            </li>
                                        })}
                                    </ul>}
                                </content-constrainer>
                            </GeneralErrorBoundary>
                        </section>
                    )
                case 'PROMO':
                    return (
                        <section className='page-section promotion-section' key={index}>
                            <GeneralErrorBoundary 
                                errorTitle="Sorry, we're having trouble showing our promotions."
                                errorMessage={errorMessage}
                            >
                                <content-constrainer class="content-constrainer--no-border">
                                    <PromotionsRowModule
                                        title={carouselName}
                                        promos={slides}
                                        handleClicks={handleClicks}
                                    />
                                </content-constrainer>
                                { (index < carousels.promoCarousels.length - 1) && <hr /> }
                            </GeneralErrorBoundary>
                        </section>
                    )
                default:
                    return null;
            }
        }
    
        // Render the sections in the desired order
        if (isSuccess) return (
            <>
                {(carousels.bannerCarousels.length > 0) && carousels.bannerCarousels.map(renderCarousel)}
                <section className="page-section on-now-section">
                    <GeneralErrorBoundary 
                        errorTitle="An error has been encounter while getting the currently airing program."
                        errorMessage={errorMessage}
                    >
                        <content-constrainer>
                            <NowPlayingContainer 
                                showEpisodeNumber={false} 
                                network={NETWORK} 
                                scheduleEndpoint={SCHEDULE_ENDPOINT}
                                fallbackImage={FALLBACK_IMAGE} 
                                ProgramAirdateProps={ProgramAirdateProps}
                                getResource={getResource}
                            />
                        </content-constrainer>
                    </GeneralErrorBoundary>
                </section>
                <section className="page-section featured-show-list-section">
                    { (carousels.featuredCarousels.length > 0) && carousels.featuredCarousels.map(renderCarousel)}
                    { (carousels.featuredCarousels.length === 0) &&    
                        <GeneralErrorBoundary 
                            errorTitle="Sorry, we're having trouble getting our currently airing show."
                            errorMessage={errorMessage}
                        >
                            <content-constrainer class="content-constrainer--no-border content-constrainer--margin-bottom">
                                <h2 className='h2 h2--extra-bold'>Featured Shows</h2>
                                <GetFeaturedListContainer
                                    type='series' 
                                    numberOfPrograms={4}
                                    startTime={'11:00'}
                                    endTime={'23:00'}
                                    handleClicks={handleClicks}
                                    hasSeriesPages={false}
                                    loadingMessage='Getting upcoming featured shows...'
                                    fallbackImage={FALLBACK_IMAGE}
                                    getResource={getResource}
                                    SCHEDULE_ENDPOINT={SCHEDULE_ENDPOINT}
                                    NETWORK={NETWORK}
                                />
                            </content-constrainer>
                        </GeneralErrorBoundary>
                    }
                </section>
                <section className="page-section featured-show-list-section">
                    <GeneralErrorBoundary 
                        errorTitle="Sorry, we're having trouble getting our currently airing show."
                        errorMessage={errorMessage}
                    >
                        <content-constrainer class="content-constrainer--no-border">
                            <h2 className='h2 h2--extra-bold'>Featured Movies</h2>
                            <GetFeaturedListContainer 
                                type='movies' 
                                numberOfPrograms={4} 
                                startTime={'20:00'} 
                                endTime={'23:30'}
                                canUseSameDay={true}
                                handleClicks={handleClicks}
                                hasSeriesPages={false}
                                loadingMessage='Getting upcoming featured movies...'
                                fallbackImage={FALLBACK_IMAGE}
                                getResource={getResource}
                                SCHEDULE_ENDPOINT={SCHEDULE_ENDPOINT}
                                NETWORK={NETWORK}
                            />
                            { (carousels.promoCarousels.length > 0) && <hr />}
                        </content-constrainer>
                    </GeneralErrorBoundary>
                </section>                
                {(carousels.promoCarousels.length > 0) && carousels.promoCarousels.map(renderCarousel)}
                {(carousels.twoColumnCarousels.length > 0) && carousels.twoColumnCarousels.map(renderCarousel)}
            </>
        )

        if (isError) return (
            <>
                <section className="page-section on-now-section">
                    <GeneralErrorBoundary 
                        errorTitle="Sorry, we're having trouble getting our currently airing show."
                        errorMessage={errorMessage}
                    >
                        <content-constrainer>
                            <NowPlayingContainer  
                                showEpisodeNumber={false} 
                                network={NETWORK} 
                                scheduleEndpoint={SCHEDULE_ENDPOINT}
                                fallbackImage={FALLBACK_IMAGE} 
                                ProgramAirdateProps={ProgramAirdateProps}
                                getResource={getResource}
                            />
                        </content-constrainer>
                    </GeneralErrorBoundary>
                </section>
               <section className="page-section">
                    <content-constrainer class="content-constrainer--no-border">
                        <ErrorHandler
                            title="Sorry, we're having some issues with our home page."
                            message={`Please try again later. If the problem persists contact us at <a href="https://support.iontelevision.com/support/home">our support page</a>.`}
                        />
                    </content-constrainer>
                </section>
            </>
        )
    }
    /**
     * Parse carousel data and add event listeners once
     * the page data has been fetched
     */
    useEffect(() => {
        if (isSuccess) {
            const bannerCarousels = data.filter(carousel => carousel.type === 'BANNER');
            const featuredCarousels = data.filter(carousel => carousel.type === 'FEATURED');
            const promoCarousels = data.filter(carousel => carousel.type === 'PROMO');
            const twoColumnCarousels = data.filter(carousel => carousel.type === 'TWO_COLUMN');
    
            setCarousels({ 
                bannerCarousels: [...bannerCarousels], 
                featuredCarousels: [...featuredCarousels], 
                promoCarousels: [...promoCarousels], 
                twoColumnCarousels: [...twoColumnCarousels] 
            })
        }

    }, [isSuccess, data])

    return (
        <>
            <SEO title="Laff | You Know You Want To" />  
            { (isSuccess || isError) && renderSections() }
            <ModalContainer {...modalProps} onModalClose={onModalClose} />
            <ModalContainer modalId="upcoming-modal" onModalClose={onModalClose}>
                <UpcomingModalContainer />
            </ModalContainer>
        </>
    )
}
